// Nurse-facing PDF manual generator.
// Produces a short, illustrated guide the coordinator can share by WhatsApp.
// Uses jsPDF (already installed) with the same brand palette as the care-plan PDF.

import jsPDF from "jspdf";

const BRAND = {
  navy: [30, 58, 138],
  blue: [29, 78, 216],
  cyan: [8, 145, 178],
  light: [239, 246, 255],
  slate: [100, 116, 139],
  ink: [15, 23, 42],
  border: [203, 213, 225],
  white: [255, 255, 255],
  green: [22, 163, 74],
  red: [220, 38, 38],
  yellow: [217, 119, 6],
};

// Cache the SVG logo rasterized once per session.
let _logoCache = null;
async function loadLogo() {
  if (_logoCache !== null) return _logoCache;
  try {
    const res = await fetch("/assets/logo.svg", { cache: "force-cache" });
    if (!res.ok) throw new Error("logo fetch failed");
    const svg = await res.text();
    const blob = new Blob([svg], { type: "image/svg+xml;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const dataUrl = await new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        try {
          const canvas = document.createElement("canvas");
          canvas.width = 1024;
          canvas.height = 1024;
          const ctx = canvas.getContext("2d");
          ctx.drawImage(img, 0, 0, 1024, 1024);
          URL.revokeObjectURL(url);
          resolve(canvas.toDataURL("image/png"));
        } catch (err) {
          reject(err);
        }
      };
      img.onerror = (e) => {
        URL.revokeObjectURL(url);
        reject(e);
      };
      img.src = url;
    });
    _logoCache = dataUrl;
    return dataUrl;
  } catch (e) {
    _logoCache = false;
    return null;
  }
}

function drawPageHeader(doc, logo) {
  const w = doc.internal.pageSize.getWidth();
  doc.setFillColor(...BRAND.navy);
  doc.rect(0, 0, w, 14, "F");
  doc.setFillColor(...BRAND.blue);
  doc.rect(0, 14, w, 3, "F");
  if (logo) {
    doc.setFillColor(...BRAND.white);
    doc.circle(10, 8.5, 5, "F");
    try {
      doc.addImage(logo, "PNG", 6, 4, 9, 9, undefined, "FAST");
    } catch (_e) {
      /* logo optional */
    }
  }
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  doc.setTextColor(...BRAND.white);
  doc.text("CIUDAD ENFERMERIA - Manual para Enfermeras", 18, 9);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(7);
  doc.text("v1.1 - Programa de cuidados en domicilio", w - 12, 9, { align: "right" });
}

function drawPageFooter(doc, pageNum, total) {
  const w = doc.internal.pageSize.getWidth();
  const h = doc.internal.pageSize.getHeight();
  doc.setDrawColor(...BRAND.border);
  doc.setLineWidth(0.2);
  doc.line(12, h - 12, w - 12, h - 12);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.setTextColor(...BRAND.slate);
  doc.text("Ciudad Enfermeria - Cuidado con calidez profesional", 12, h - 7);
  doc.text(`Pagina ${pageNum} de ${total}`, w - 12, h - 7, { align: "right" });
}

function newPage(doc, logo) {
  doc.addPage();
  drawPageHeader(doc, logo);
}

function ensureSpace(doc, y, needed, logo) {
  const h = doc.internal.pageSize.getHeight();
  if (y + needed > h - 15) {
    newPage(doc, logo);
    return 24;
  }
  return y;
}

function sectionTitle(doc, y, num, title, color = BRAND.blue) {
  const x = 12;
  // Circle with step number
  doc.setFillColor(...color);
  doc.circle(x + 4, y - 1, 4.5, "F");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.setTextColor(...BRAND.white);
  doc.text(String(num), x + 4, y + 0.8, { align: "center" });
  // Title
  doc.setTextColor(...BRAND.ink);
  doc.setFontSize(13);
  doc.text(title, x + 12, y + 1);
  // Underline
  doc.setDrawColor(...color);
  doc.setLineWidth(0.4);
  doc.line(x + 12, y + 3, x + 12 + doc.getTextWidth(title), y + 3);
  return y + 9;
}

function bullet(doc, y, text, opts = {}) {
  const x = 16;
  const maxW = doc.internal.pageSize.getWidth() - x - 14;
  doc.setFillColor(...(opts.color || BRAND.blue));
  doc.circle(x, y - 1.2, 0.9, "F");
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.setTextColor(...BRAND.ink);
  const lines = doc.splitTextToSize(text, maxW);
  doc.text(lines, x + 3, y);
  return y + lines.length * 4.6 + 1;
}

function boldBullet(doc, y, label, text, color = BRAND.navy) {
  const x = 16;
  const maxW = doc.internal.pageSize.getWidth() - x - 14;
  doc.setFillColor(...color);
  doc.circle(x, y - 1.2, 0.9, "F");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.setTextColor(...color);
  doc.text(label, x + 3, y);
  const labelW = doc.getTextWidth(label);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(...BRAND.ink);
  const lines = doc.splitTextToSize(text, maxW - labelW - 1);
  doc.text(lines, x + 3 + labelW + 1, y);
  return y + lines.length * 4.6 + 1;
}

function paragraph(doc, y, text, opts = {}) {
  const x = 12;
  const maxW = doc.internal.pageSize.getWidth() - x - 12;
  doc.setFont("helvetica", opts.bold ? "bold" : "normal");
  doc.setFontSize(opts.size || 10);
  doc.setTextColor(...(opts.color || BRAND.ink));
  const lines = doc.splitTextToSize(text, maxW);
  doc.text(lines, x, y);
  return y + lines.length * (opts.lineHeight || 4.8) + (opts.gap || 2);
}

function calloutBox(doc, y, title, body, tone = "info") {
  const x = 12;
  const maxW = doc.internal.pageSize.getWidth() - x - 12;
  const palette = {
    info: { bg: BRAND.light, border: BRAND.blue, text: BRAND.navy },
    warn: { bg: [254, 249, 195], border: BRAND.yellow, text: [120, 53, 15] },
    danger: { bg: [254, 226, 226], border: BRAND.red, text: [127, 29, 29] },
    ok: { bg: [220, 252, 231], border: BRAND.green, text: [20, 83, 45] },
  }[tone];
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  const bodyLines = doc.splitTextToSize(body, maxW - 6);
  const boxH = 10 + bodyLines.length * 4.6;
  doc.setFillColor(...palette.bg);
  doc.setDrawColor(...palette.border);
  doc.setLineWidth(0.4);
  doc.roundedRect(x, y, maxW, boxH, 2, 2, "FD");
  doc.setTextColor(...palette.text);
  doc.text(title, x + 3, y + 5);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(...palette.text);
  doc.text(bodyLines, x + 3, y + 10);
  return y + boxH + 3;
}

// -------------------------------------------------
// COVER PAGE
// -------------------------------------------------
function drawCover(doc, logo) {
  const w = doc.internal.pageSize.getWidth();
  const h = doc.internal.pageSize.getHeight();

  // Full page navy background
  doc.setFillColor(...BRAND.navy);
  doc.rect(0, 0, w, h, "F");
  // Diagonal accent
  doc.setFillColor(...BRAND.blue);
  doc.triangle(0, h * 0.55, w, h * 0.35, w, h, "F");
  doc.setFillColor(...BRAND.cyan);
  doc.triangle(0, h * 0.72, w, h * 0.58, w, h, "F");

  // Logo (large, centered)
  if (logo) {
    const cx = w / 2 - 22;
    const cy = 40;
    doc.setFillColor(...BRAND.white);
    doc.circle(w / 2, cy + 22, 26, "F");
    try {
      doc.addImage(logo, "PNG", cx, cy, 44, 44, undefined, "FAST");
    } catch (_e) {
      /* logo optional */
    }
  }

  // Institution
  doc.setFont("helvetica", "bold");
  doc.setFontSize(22);
  doc.setTextColor(...BRAND.white);
  doc.text("CIUDAD ENFERMERIA", w / 2, 105, { align: "center" });

  doc.setFontSize(11);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(219, 234, 254);
  doc.text("Cuidado con calidez profesional", w / 2, 112, { align: "center" });

  // Main title (badge)
  doc.setFillColor(...BRAND.white);
  doc.roundedRect(w / 2 - 65, 132, 130, 26, 3, 3, "F");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(18);
  doc.setTextColor(...BRAND.navy);
  doc.text("Manual para", w / 2, 143, { align: "center" });
  doc.setFontSize(20);
  doc.setTextColor(...BRAND.blue);
  doc.text("Enfermeras y Cuidadoras", w / 2, 153, { align: "center" });

  // Subtitle
  doc.setFont("helvetica", "normal");
  doc.setFontSize(11);
  doc.setTextColor(...BRAND.white);
  doc.text(
    "Guia rapida para usar la app del Programa de Cuidados",
    w / 2, 170, { align: "center" }
  );

  // Footer
  doc.setFontSize(9);
  doc.setTextColor(219, 234, 254);
  doc.text(
    `Version 1.1 - ${new Date().toLocaleDateString("es-MX", { month: "long", year: "numeric" })}`,
    w / 2, h - 20, { align: "center" }
  );
  doc.text("programa.ciudadenfermeria.com", w / 2, h - 14, { align: "center" });
}

// -------------------------------------------------
// MAIN
// -------------------------------------------------
export async function generateNurseManualPdf() {
  const doc = new jsPDF({ unit: "mm", format: "a4" });
  const logo = await loadLogo();

  // Page 1: Cover
  drawCover(doc, logo);

  // Page 2: Welcome + Installation
  newPage(doc, logo);
  let y = 24;
  y = paragraph(
    doc,
    y,
    "Bienvenida",
    { bold: true, size: 20, color: BRAND.navy, gap: 3 }
  );
  y = paragraph(
    doc,
    y,
    "Esta guia te explica en 11 pasos como usar la aplicacion para registrar el programa de cuidados de tus pacientes. La app funciona en tu celular y no necesitas ser experta en tecnologia. Cualquier duda, contacta al coordinador.",
    { lineHeight: 5, gap: 5 }
  );

  y = sectionTitle(doc, y, 1, "Instalar la app en tu celular");
  y = paragraph(
    doc,
    y,
    "La app se abre desde el navegador de tu celular. Al instalarla queda como una aplicacion normal en tu pantalla de inicio.",
    { gap: 3 }
  );
  y = boldBullet(doc, y, "iPhone (Safari):", "Abre programa.ciudadenfermeria.com. Toca el boton compartir (cuadrado con flecha hacia arriba). Selecciona 'Anadir a pantalla de inicio' y luego 'Anadir'.");
  y = boldBullet(doc, y, "Android (Chrome):", "Abre programa.ciudadenfermeria.com. Toca el menu de 3 puntos arriba a la derecha. Selecciona 'Instalar aplicacion' o 'Anadir a pantalla de inicio'.");
  y = calloutBox(
    doc, y + 1,
    "Consejo",
    "Una vez instalada, veras el icono de Ciudad Enfermeria en tu pantalla. Abre la app desde ahi cada vez que vayas a trabajar.",
    "info"
  );

  y = sectionTitle(doc, y, 2, "Iniciar sesion");
  y = paragraph(
    doc,
    y,
    "El coordinador te dara un correo y contrasena. En la primera pantalla:",
    { gap: 2 }
  );
  y = bullet(doc, y, "Escribe tu correo electronico completo.");
  y = bullet(doc, y, "Escribe tu contrasena. Toca el icono del ojo si quieres verla.");
  y = bullet(doc, y, "Toca 'Ingresar'.");
  y = calloutBox(
    doc, y + 1,
    "Importante",
    "Si olvidas tu contrasena o si por alguna razon dice 'Acceso suspendido', comunicate con tu coordinador.",
    "warn"
  );

  // Page 3: Create program + Vitals
  newPage(doc, logo);
  y = 24;
  y = sectionTitle(doc, y, 3, "Crear un nuevo programa de cuidados");
  y = paragraph(doc, y, "Al iniciar tu turno vas a crear un nuevo programa por cada paciente que atiendas:", { gap: 2 });
  y = bullet(doc, y, "En el tablero, toca el boton azul grande 'Nuevo programa de cuidados'.");
  y = bullet(doc, y, "Selecciona a tu paciente de la lista (solo aparecen los pacientes que el coordinador te ha asignado).");
  y = bullet(doc, y, "Ajusta la fecha y hora de inicio si es diferente a la actual.");
  y = bullet(doc, y, "La app guarda todo automaticamente mientras escribes. No necesitas presionar 'Guardar' cada vez.");
  y = calloutBox(
    doc, y + 1,
    "Autoguardado",
    "Veras un mensaje 'Guardado' arriba a la derecha cada vez que la app sincroniza tus datos con el servidor.",
    "ok"
  );

  y = sectionTitle(doc, y, 4, "Registrar signos vitales", BRAND.cyan);
  y = paragraph(doc, y, "La app tiene tres tomas de signos vitales por turno. Registra los valores como los mediste:", { gap: 2 });
  y = bullet(doc, y, "Presion arterial (sistolica / diastolica). Ejemplo: 120/80");
  y = bullet(doc, y, "Frecuencia cardiaca en latidos por minuto.");
  y = bullet(doc, y, "Frecuencia respiratoria y saturacion de oxigeno.");
  y = bullet(doc, y, "Temperatura en grados centigrados.");
  y = calloutBox(
    doc, y + 1,
    "Valores fuera de rango",
    "Si algun valor esta muy alto o muy bajo, la app lo marcara en rojo automaticamente y aparecera un boton verde de WhatsApp para avisar al supervisor con un solo toque.",
    "danger"
  );

  // Page 4: Interventions, Materials, Medications
  newPage(doc, logo);
  y = 24;
  y = sectionTitle(doc, y, 5, "Intervenciones, materiales y medicamentos", BRAND.blue);
  y = paragraph(doc, y, "Registra todo lo que hiciste durante el turno:", { gap: 2 });
  y = boldBullet(doc, y, "Intervenciones:", "Marca los procedimientos que realizaste (bano, cambio de pañal, curaciones, etc.). Puedes agregar una hora si es importante.");
  y = boldBullet(doc, y, "Materiales usados:", "Anota los insumos (guantes, gasas, panales, etc.). Esto ayuda al coordinador a llevar el inventario.");
  y = boldBullet(doc, y, "Medicamentos:", "Solo veras los medicamentos que el coordinador autorizo para tu paciente. Toca el que administraste, elige la dosis y la hora. Si el medicamento estaba programado y no lo diste a tiempo, la app te avisara con un aviso rojo.");
  y = calloutBox(
    doc, y + 1,
    "Nunca administres medicamentos fuera del catalogo",
    "Si el medico ordena un medicamento nuevo, comunicate primero con el coordinador. El debe autorizarlo antes de que aparezca en tu app.",
    "danger"
  );

  y = sectionTitle(doc, y, 6, "Balance de liquidos y monitoreo del sueno", BRAND.cyan);
  y = paragraph(doc, y, "Estos datos solo se llenan si tu paciente los requiere:", { gap: 2 });
  y = boldBullet(doc, y, "Ingresos y egresos:", "Registra los liquidos que tomo (agua, jugo, infusiones) y los que elimino (orina, evacuaciones). La app calcula el balance sola.");
  y = boldBullet(doc, y, "Sueno de 24 horas:", "Marca las horas que el paciente durmio en cada franja del dia. La app suma el total de horas.");
  y = boldBullet(doc, y, "Escala de Morse (riesgo de caida):", "Contesta las 6 preguntas. La app calcula el nivel de riesgo automaticamente.");

  // Page 5: Dialysis + Evidence + Save
  newPage(doc, logo);
  y = 24;
  y = sectionTitle(doc, y, 7, "Dialisis Peritoneal (solo si aplica)", BRAND.blue);
  y = paragraph(
    doc, y,
    "Si tu paciente esta en DPCA, veras una seccion especial en el programa. Registra cada recambio:",
    { gap: 2 }
  );
  y = bullet(doc, y, "Toca 'Agregar recambio' para anadir una fila nueva.");
  y = bullet(doc, y, "Captura hora, tipo de bolsa (1.5% amarilla, 2.5% verde, 4.25% roja).");
  y = bullet(doc, y, "Escribe el volumen infundido (usualmente 2000 ml).");
  y = bullet(doc, y, "Al final del intercambio, escribe el volumen drenado. La app calcula la UF (ultrafiltracion) sola.");
  y = bullet(doc, y, "Selecciona el aspecto del liquido: Claro, Turbio, Hematico o con Fibrina.");
  y = calloutBox(
    doc, y + 1,
    "Alerta automatica de peritonitis",
    "Si marcas el liquido como Turbio, Hematico o con Fibrina, la app abrira WhatsApp automaticamente con un mensaje listo para enviar al supervisor. Solo tocas 'Enviar'. La peritonitis puede ser urgente.",
    "danger"
  );
  y = bullet(doc, y, "Registra el peso corporal del paciente por la manana (en ayunas, con vejiga vacia).");

  // Page: Gastrostomia PEG
  newPage(doc, logo);
  y = 24;
  y = sectionTitle(doc, y, 8, "Gastrostomia PEG - alimentacion por sonda (solo si aplica)");
  y = paragraph(
    doc, y,
    "Si tu paciente se alimenta por sonda de gastrostomia, veras la seccion 'Gastrostomia (PEG)' en el programa. Registra cada toma:",
    { gap: 2 }
  );
  y = bullet(doc, y, "Antes de alimentar: verifica el residuo gastrico aspirando suavemente con la jeringa y anotalo en 'Residuo ml'.");
  y = bullet(doc, y, "Eleva la cabecera 30-45 grados y marca la casilla 'Cabecera'. Mantenla elevada 30-60 minutos despues de la toma.");
  y = bullet(doc, y, "Toca 'Agregar toma' y captura: hora, formula o alimento (Ensure, licuado, etc.), volumen en ml y metodo (bolo, gravedad o bomba).");
  y = bullet(doc, y, "Pasa 15-30 ml de agua antes y despues de cada toma y de cada medicamento. Anotala en 'Agua antes' y 'Agua despues'. Esto evita que la sonda se tape.");
  y = bullet(doc, y, "Marca la tolerancia: Bien tolerada, Nausea, Vomito, Regurgitacion, Distension o Diarrea.");
  y = calloutBox(
    doc, y + 1,
    "Alerta automatica de broncoaspiracion",
    "Si marcas Vomito o Regurgitacion, o si el residuo gastrico es mayor a 200 ml, la app abre WhatsApp automaticamente con la alerta lista para el supervisor. Solo tocas 'Enviar'. Suspende la toma y manten al paciente semisentado.",
    "danger"
  );
  y = paragraph(doc, y + 1, "Cuidados de la sonda y el estoma (una vez por turno):", { bold: true, gap: 2 });
  y = bullet(doc, y, "Revisa la piel alrededor del estoma y marca lo que veas: sano, enrojecido, granuloma, fuga, secrecion o dolor.");
  y = bullet(doc, y, "Anota la marca externa de la sonda en centimetros. Si cambio respecto a dias anteriores, la sonda pudo moverse: avisa al coordinador.");
  y = bullet(doc, y, "Gira la sonda suavemente, limpia el estoma con agua y jabon neutro, secalo bien, y marca 'Sonda rotada' y 'Limpieza estoma'.");
  y = bullet(doc, y, "Verifica la permeabilidad: si el agua pasa sin resistencia marca 'Permeable'.");
  y = calloutBox(
    doc, y + 1,
    "Si la sonda se tapa o se sale",
    "Selecciona 'Obstruida' o 'Salida accidental' en Permeabilidad: la app avisara al supervisor por WhatsApp automaticamente. El estoma puede cerrarse en pocas horas, por eso la reposicion de la sonda es urgente. No intentes recolocarla tu misma.",
    "warn"
  );

  // Page: Evidence + Save/Share
  newPage(doc, logo);
  y = 24;
  y = sectionTitle(doc, y, 9, "Evidencias (fotos y videos)", BRAND.cyan);
  y = paragraph(doc, y, "Puedes anadir hasta 12 evidencias por programa:", { gap: 2 });
  y = bullet(doc, y, "Toca 'Anadir evidencia'. Elige 'Camara' o 'Galeria'.");
  y = bullet(doc, y, "La app reduce las fotos automaticamente para que suban mas rapido.");
  y = bullet(doc, y, "Ejemplos: fotos del sitio de curacion, estado de la piel, valores de glucometro, etiquetas de medicamentos, etc.");

  // Save/Share continues on the same page
  y = sectionTitle(doc, y + 2, 10, "Guardar y compartir el reporte", BRAND.blue);
  y = paragraph(doc, y, "Al terminar tu turno:", { gap: 2 });
  y = bullet(doc, y, "Revisa que todos los datos importantes esten completos.");
  y = boldBullet(doc, y, "Descargar PDF:", "Toca el boton 'Descargar PDF'. Genera un reporte institucional que puedes guardar o imprimir.");
  y = boldBullet(doc, y, "Compartir por WhatsApp:", "Toca el boton verde. Se abre WhatsApp con un resumen del turno listo para enviar al supervisor o al familiar.");
  y = boldBullet(doc, y, "Boton rojo (emergencia):", "Aparece automaticamente cuando hay valores criticos (signos vitales fuera de rango, liquido turbio, medicamento vencido). Abre WhatsApp con una alerta urgente al supervisor.");
  y = calloutBox(
    doc, y + 1,
    "Configurar el numero del supervisor",
    "La primera vez que uses el boton verde o rojo, la app te pedira el numero de WhatsApp del supervisor. Escribelo con lada (ejemplo: 5215551234567). Se guarda solo en tu celular.",
    "info"
  );

  // Final tips (own page)
  newPage(doc, logo);
  y = 24;
  y = sectionTitle(doc, y, 11, "Consejos importantes", BRAND.navy);
  y = bullet(doc, y, "Todo se guarda automaticamente: si se corta el internet, la app sincroniza cuando vuelvas a tener senal.");
  y = bullet(doc, y, "No compartas tu contrasena con nadie mas.");
  y = bullet(doc, y, "Cierra sesion desde el menu si prestas tu celular a alguien.");
  y = bullet(doc, y, "El coordinador ve todos los programas y puede darte retroalimentacion.");
  y = bullet(doc, y, "Ante cualquier duda, llama al coordinador antes de dejar al paciente.");

  y = calloutBox(
    doc, y + 3,
    "Contacto",
    "Coordinador Ciudad Enfermeria: 55 XXXX XXXX  -  contacto@ciudadenfermeria.com",
    "ok"
  );

  // Add headers/footers to every page except the cover
  const total = doc.internal.getNumberOfPages();
  for (let i = 2; i <= total; i++) {
    doc.setPage(i);
    drawPageFooter(doc, i - 1, total - 1);
  }

  const fname = `Manual_Enfermeras_CiudadEnfermeria_v1.1.pdf`;
  doc.save(fname);
  return fname;
}
